import type { SessionOneTimePurchases, SessionUser } from "types/session-user";

export type WorkspaceOtpSnapshot = {
  total_licenses: number;
  available_licenses: number;
  used_licenses?: number;
  latest_purchase_date?: Date | string | null;
  has_available_trial: boolean;
  has_active_trial: boolean;
  trial_days_remaining?: number;
  trial_expiry_date?: Date | string | null;
};

export type OtpCheckoutBaseline = {
  total_licenses: number;
  available_licenses: number;
  used_licenses: number;
  latest_purchase_date: string | null;
};

export type ProjectQuotaApiResponse = {
  one_time_purchases?: WorkspaceOtpSnapshot | null;
  workspaces?: Record<
    string,
    {
      one_time_purchases?: WorkspaceOtpSnapshot | null;
    }
  >;
};

const RECENT_PURCHASE_WINDOW_MS = 2 * 60 * 60 * 1000;

const asWorkspaceOtpSnapshot = (
  purchases: SessionOneTimePurchases | null | undefined
): WorkspaceOtpSnapshot | null => purchases ?? null;

export const resolveWorkspaceOtpFromQuotas = (
  quotaData: ProjectQuotaApiResponse | null | undefined,
  workspaceId: string | null | undefined
): WorkspaceOtpSnapshot | null => {
  if (!quotaData) {
    return null;
  }
  if (workspaceId && quotaData.workspaces) {
    const entry = quotaData.workspaces[workspaceId];
    if (entry?.one_time_purchases) {
      return entry.one_time_purchases;
    }
  }
  return quotaData.one_time_purchases ?? null;
};

export const isOtpLicensePoolReady = (otp: WorkspaceOtpSnapshot | null | undefined): boolean =>
  (otp?.available_licenses ?? 0) > 0;

export const toOtpCheckoutBaseline = (
  purchases: SessionOneTimePurchases | WorkspaceOtpSnapshot | null | undefined
): OtpCheckoutBaseline => ({
  total_licenses: purchases?.total_licenses ?? 0,
  available_licenses: purchases?.available_licenses ?? 0,
  used_licenses: purchases?.used_licenses ?? 0,
  latest_purchase_date: purchases?.latest_purchase_date
    ? String(purchases.latest_purchase_date)
    : null,
});

const parsePurchaseDate = (value: Date | string | null | undefined): number | null => {
  if (!value) {
    return null;
  }
  const parsed = Date.parse(String(value));
  return Number.isNaN(parsed) ? null : parsed;
};

export const isRecentOtpPurchase = (
  otp: WorkspaceOtpSnapshot | null | undefined,
  nowMs: number = Date.now(),
  windowMs: number = RECENT_PURCHASE_WINDOW_MS
): boolean => {
  const purchasedAt = parsePurchaseDate(otp?.latest_purchase_date);
  if (purchasedAt == null) {
    return false;
  }
  return nowMs - purchasedAt <= windowMs;
};

export const hasOtpPurchaseProgressed = (
  current: WorkspaceOtpSnapshot | null | undefined,
  baseline: OtpCheckoutBaseline | null | undefined
): boolean => {
  if (!current || !baseline) {
    return false;
  }

  if ((current.total_licenses ?? 0) > baseline.total_licenses) {
    return true;
  }
  if ((current.available_licenses ?? 0) > baseline.available_licenses) {
    return true;
  }
  if ((current.used_licenses ?? 0) > baseline.used_licenses) {
    return true;
  }

  const currentDate = parsePurchaseDate(current.latest_purchase_date);
  const baselineDate = parsePurchaseDate(baseline.latest_purchase_date);
  if (currentDate != null && (baselineDate == null || currentDate > baselineDate)) {
    return true;
  }

  return false;
};

export type ProjectLicenseReadyPayload = {
  purchase_type?: string | null;
  one_time_purchase_id?: number | null;
  trial_expiry_date?: string | null;
  permissions?: {
    projectInfo?: {
      isLicensed?: boolean;
      is_licensed?: boolean;
    };
  };
};

export const isProjectLicensedFromPayload = (
  project: ProjectLicenseReadyPayload | null | undefined
): boolean => {
  if (!project) {
    return false;
  }
  const info = project.permissions?.projectInfo;
  if (typeof info?.isLicensed === "boolean") {
    return info.isLicensed;
  }
  if (typeof info?.is_licensed === "boolean") {
    return info.is_licensed;
  }
  if (project.trial_expiry_date) {
    return false;
  }
  return project.purchase_type === "one_time" && project.one_time_purchase_id != null;
};

export const isOtpPurchaseVerificationReady = (params: {
  session: SessionUser;
  workspaceId?: string | null;
  quotaData?: ProjectQuotaApiResponse | null;
  projectPayload?: ProjectLicenseReadyPayload | null;
  baseline?: OtpCheckoutBaseline | null;
}): boolean => {
  const { session, workspaceId, quotaData, projectPayload, baseline } = params;

  if (projectPayload && isProjectLicensedFromPayload(projectPayload)) {
    return true;
  }

  const otpFromQuota = resolveWorkspaceOtpFromQuotas(quotaData, workspaceId);
  const otpFromSession = asWorkspaceOtpSnapshot(session.one_time_purchases);

  if (isOtpLicensePoolReady(otpFromQuota) || isOtpLicensePoolReady(otpFromSession)) {
    return true;
  }

  if (baseline) {
    if (hasOtpPurchaseProgressed(otpFromQuota, baseline)) {
      return true;
    }
    if (hasOtpPurchaseProgressed(otpFromSession, baseline)) {
      return true;
    }
  }

  // Success-url refresh without a stored baseline: a very recent purchase is enough.
  if (!baseline && (isRecentOtpPurchase(otpFromQuota) || isRecentOtpPurchase(otpFromSession))) {
    return true;
  }

  return false;
};
